import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  LoginRememberMeUtilsService,
  UserSessionService
} from '@com-delta-idp/mnx-oauth';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Observable, of } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { CookieHelper } from '../../common/cookie-helper';
import { DataLayer } from '../../common/datalayer/datalayer';
import { ApplicationConfig } from '../../config/config';
import { CommonService } from '../common.service';

@Injectable({
  providedIn: 'root'
})
export class AccountSummaryService {
  aemErrorResp: any;
  aemErrorMsgDetails: any;
  private data = null;
  private errorData: any;
  private observable: Observable<any>;
  private observableError: Observable<any>;
  private notificationData: any;
  private isGetDbrd = false;

  constructor(
    private http: HttpClient,
    private config: ApplicationConfig,
    private srv: CommonService,
    private dataLayer: DataLayer,
    private cookieHelper: CookieHelper,
    private loggingService: LoggingService,
    private userSession: UserSessionService,
    private loginRememberMeUtils: LoginRememberMeUtilsService
  ) {}

  /*METHOD TO GET ACCOUNT SUMMARY DETAILS*/
  getAccountSummaryDetails(loginFlag?: boolean): Observable<any> {
    const customerHeader = new HttpHeaders({
      'Content-type': 'application/json; charset=utf-8'
    });
    customerHeader.append('Accept', 'application/json');
    if (this.dataLayer.checkForSettingDdcfbData(this.data)) {
      this.dataLayer.setDdcfbData(this.data, loginFlag);
    }
    this.dataLayer.setCustomerInfo(this.data, this.config);
    this.dataLayer.setWarmStateFlag(this.data);
    if (this.data && !loginFlag && !this.isGetDbrd) {
      return of(this.data);
    } else if (this.data == null && !loginFlag && this.isGetDbrd) {
      if (this.cookieHelper.getCookieValue('accountType')) {
        this.cookieHelper.deleteCookie('accountType');
      }
      return of(this.data);
    } else if (this.observable) {
      return this.observable;
    } else {
      this.observable = this.userSession.getUserSession().pipe(
        map(
          (response) => {
            this.observable = null;
            if (Object.keys(response).length > 0) {
              this.data = response && response['smNumber'] ? response : null;
              if (this.dataLayer.checkForSettingDdcfbData(this.data)) {
                this.dataLayer.setDdcfbData(this.data, loginFlag);
              }
              this.dataLayer.setWarmStateFlag(this.data);
              if (response && response['smNumber']) {
                if (this.loginRememberMeUtils.checkServiceWorkerController()) {
                  const event = new CustomEvent('service-worker-token', {
                    bubbles: true
                  });
                  document.dispatchEvent(event);
                }
              }
              if (response['isWarmState']) {
                if (!this.cookieHelper.getCookieValue('accountType')) {
                  this.cookieHelper.setCookieName(
                    'accountType',
                    'true-false',
                    0,
                    true
                  );
                }
                if (this.cookieHelper.getCookieValue('prompt')) {
                  this.cookieHelper.deleteCookie('prompt');
                }
              }
              this.isGetDbrd = false;
            } else {
              this.data = null;
              this.isGetDbrd = true;
              if (this.cookieHelper.getCookieValue('accountType')) {
                this.cookieHelper.deleteCookie('accountType');
              }
            }
            return this.data;
          },
          (err) => {
            this.loggingService.log(
              'Caught Error in Accountsummary details: ' + err
            );
          }
        ),
        share()
      );
      return this.observable;
    }
  }

  public getAEMErrorMessage(): Observable<any> {
    if (this.errorData) {
      return of(this.errorData);
    } else if (this.observableError) {
      return this.observableError;
    } else {
      this.observableError = this.http
        .get(`${this.config.get('APP_MESSAGES_ERROR_URL')}`)
        .pipe(
          map((response) => {
            this.observableError = null;
            this.errorData = response;
            return this.errorData;
          }),
          share()
        );
      return this.observableError;
    }
  }

  public populateErrorMessage(errorCodeData) {
    this.getAEMErrorMessage().subscribe(
      (data) => {
        this.aemErrorResp = data;
        this.aemErrorMsgDetails = this.getErrorMessage(
          errorCodeData,
          this.aemErrorResp.errormessages
        );
      },
      (err) => {
        this.loggingService.log('Caught Error: ' + err);
      }
    );
    return this.aemErrorMsgDetails;
  }

  public getErrorMessage(errorCode, aemErrorResp) {
    for (const errormsg of aemErrorResp) {
      if (errorCode.toUpperCase() === errormsg.errorId.toUpperCase()) {
        return errormsg;
      }
    }
  }

  public getTripNotification(confirmationNumber: string): Observable<any> {
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    const pnrNbr = { pnrNbr: confirmationNumber };
    if (this.notificationData) {
      return of(this.notificationData);
    } else if (this.observable) {
      return this.observable;
    } else {
      this.observable = this.http
        .post(
          `${this.config.get(
            'APP_TRIPS_NOTIFICATION_URL'
          )}?pnrNbr=${confirmationNumber}`,
          { headers: customheaders }
        )
        .pipe(
          map((res) => {
            this.observable = null;
            this.notificationData = res;
            return this.notificationData;
          }),
          share()
        );
      return this.observable;
    }
  }

  public getCrpAdminList(): Observable<any> {
    if (this.errorData) {
      return of(this.errorData);
    } else {
      const config = {
        headers: {
          Accept: 'application/json;charset=UTF-8',
          appId: 'CUS',
          channelId: 'ecom'
        }
      };
      this.observable = this.http
        .get('/customer/getLoginCrptAdminList', config)
        .pipe(
          map(
            (response) => {
              this.observable = null;
              this.errorData = response;
              return this.errorData;
            },
            (err) => {
              this.loggingService.log(
                'Caught Error in getCrpAdminList details: ' + err
              );
            }
          ),
          share()
        );
      return this.observable;
    }
  }

  postAccountType(acctType: string): Observable<any> {
    const customerHeader = new HttpHeaders({
      'Content-type': 'application/json; charset=utf-8'
    });
    customerHeader.append('Accept', 'application/json');
    this.observable = this.http
      .post(
        `${this.config.get('APP_SESS_CUSTOMER_EDP_DATA_URL')}`,
        {
          cacheKey: 'edpCorpData',
          value: acctType
        },
        { headers: customerHeader, responseType: 'json' }
      )
      .pipe(
        map((response) => {
          this.observable = null;
          return response;
        }),
        share()
      );
    return this.observable;
    // }
  }

  UpdateAccountType(acctType: string): Observable<any> {
    const customerHeader = new HttpHeaders({
      'Content-type': 'application/json; charset=utf-8'
    });
    customerHeader.append('Accept', 'application/json');
    this.observable = this.http
      .post(
        `${this.config.get('APP_SESS_CUSTOMER_EDP_DATAUPDATE_URL')}`,
        {
          cacheKey: 'edpCorpData',
          value: acctType
        },
        { headers: customerHeader, responseType: 'json' }
      )
      .pipe(
        map((response) => {
          this.observable = null;
          return response;
        }),
        share()
      );
    return this.observable;
    // }
  }

  getAccountType(cacheKey: string): Observable<any> {
    const customerHeader = new HttpHeaders({
      'Content-type': 'application/json; charset=utf-8'
    });
    customerHeader.append('Accept', 'application/json');
    this.observable = this.http
      .get(
        `${this.config.get(
          'APP_SESS_CUSTOMER_EDP_RETRIEVE_URL'
        )}?CacheKey=${cacheKey}`,
        { headers: customerHeader, responseType: 'json' }
      )
      .pipe(
        map((response) => {
          this.observable = null;
          return response;
        }),
        share()
      );
    return this.observable;
  }

  // public getDummyLogin() {
  // const loginDetails = { 'smNumber': '2487111896', 'firstName': 'Qagoldfour', 'lastName': 'Testacct', 'smBalance': '3535000', 'membershipLevel': 'GM', 'enrollmentDate': '2004-08-16', 'prefix': 'Mr', 'primaryEmailAddress': 'edward.cooper@delta.com', 'medallionMemberDesc': 'Gold Medallion', 'medallionMbrInd': '1', 'country': 'US', 'language': 'en', 'customerID': '1000000000673', 'MQMBalance': '0', 'loyaltyPromoInd': 'Y', 'loyaltyPromoLink': '/mypromos/displayPromotions', 'sysDrvdPrefHomeArptCd': 'LGA', 'crprtAgrmntId': '504654|7781966|7802739|6308529|502096', 'crprtAgrmntTypeCode': 'CNA|CNA|CNA|CNA|CNA', 'crprtPricingDiscCode': 'BQXO00|56HLU00|56YKD00|474SO00|BOUL00', 'crprtName': 'Cognizant Technology Solutions|410 Bridge|17hats|77 Global|Bridges Test Account', 'skyPriority': true, 'pwmMbr': false, 'crprtAgrmntFound': true, 'skymilesMember': true, 'langPromptIndicator': false, 'rememberMeIndicator': false, 'millionMiler': false, 'irop': false, 'spgInd': false, 'firstTime': true, 'hasUserNm': false, 'is360Tier': false, 'promoCdInd': false, 'loggedIn': true, 'isWarmState': false, 'hasSQA': true, 'hasEmail': true, 'canBookNonRefundable': 'true|false|NA|false|true|NA|false' };
  // return loginDetails;
  // }
  // public getretrivedetails() {
  //   const retrivedetails =  {
  //    'cacheKey':  'edpCorpData',
  //    'value': 'B: 1'
  //  };
  //  return retrivedetails;
  // }
  private handleError(error: any) {
    return Observable.throw(error.statusText);
  }
}
